import React from 'react';
import { Button } from './button';
import { cn } from '@/lib/utils';
import { ChevronUp, ChevronDown } from 'lucide-react';

interface SortableHeaderProps {
  children: React.ReactNode;
  sortKey: string;
  currentSort: string;
  onSort: (sortType: string) => void;
  className?: string;
  disabled?: boolean;
}

export function SortableHeader({
  children,
  sortKey,
  currentSort,
  onSort,
  className,
  disabled = false
}: SortableHeaderProps) {
  // Determine current sort direction for this column
  const getSortDirection = () => {
    if (!currentSort || !currentSort.startsWith(sortKey)) return 'default';
    return currentSort.endsWith('-asc') ? 'asc' : 'desc';
  };

  // Get next sort type when clicked
  const getNextSortType = () => {
    const currentDirection = getSortDirection();
    if (currentDirection === 'default') return `${sortKey}-desc`;
    return currentDirection === 'desc' ? `${sortKey}-asc` : '';
  };

  const handleClick = () => {
    if (disabled) return;
    const nextSort = getNextSortType();
    onSort(nextSort);
  };

  const sortDirection = getSortDirection();

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={handleClick}
      disabled={disabled}
      className={cn(
        "h-auto p-0 font-medium text-muted-foreground hover:bg-accent/10 hover:text-foreground button-focus-internal justify-start gap-1",
        className
      )}
      aria-label={`Ordenar por ${children?.toString()} ${sortDirection === 'asc' ? 'crescente' : sortDirection === 'desc' ? 'decrescente' : 'clique para ordenar'}`}
      aria-sort={sortDirection === 'asc' ? 'ascending' : sortDirection === 'desc' ? 'descending' : 'none'}
    >
      {children}
      {sortDirection === 'asc' ? (
        <ChevronUp className="h-4 w-4 shrink-0 text-foreground" />
      ) : sortDirection === 'desc' ? (
        <ChevronDown className="h-4 w-4 shrink-0 text-foreground" />
      ) : (
        <div className="flex flex-col gap-0.5 shrink-0">
          <ChevronUp className="h-3 w-3 text-muted-foreground/60" />
          <ChevronDown className="h-3 w-3 text-muted-foreground/60" />
        </div>
      )}
    </Button>
  );
}